<script lang="ts">
	import CheckIcon from "@lucide/svelte/icons/check";
	import MinusIcon from "@lucide/svelte/icons/minus";
	import { Section, type SectionTheme } from "#lib/web-ui/primitives/index.js";
	import { GlossaryOpenButton, RichText, type RichTextSegment } from "./glossary";

	interface DeductionItem {
		cost: boolean;
		segments: RichTextSegment[];
	}

	interface DeductionColumn {
		title: string;
		items: DeductionItem[];
	}

	interface Props {
		title: string;
		lede: RichTextSegment[];
		hint: string;
		costLabel: string;
		noCostLabel: string;
		columns: DeductionColumn[];
		note: string;
		theme?: SectionTheme;
		id?: string;
	}

	let {
		title,
		lede,
		hint,
		costLabel,
		noCostLabel,
		columns,
		note,
		theme = "dark",
		id
	}: Props = $props();
</script>

<Section {theme} {id} data-testid="associate-deductions">
	<div>
		<h2 class="text-foreground text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
		<p class="text-muted-foreground mt-6 text-lg">
			<RichText segments={lede} />
		</p>
		<p class="text-muted-foreground mt-4 text-sm">
			{hint}
			<GlossaryOpenButton />.
		</p>
	</div>

	<div class="divide-border mt-12 grid gap-10 md:grid-cols-3 md:gap-8 md:divide-x">
		{#each columns as column (column.title)}
			<div class="md:px-6 md:first:pl-0 md:last:pr-0">
				<h3 class="text-foreground text-lg font-semibold">{column.title}</h3>
				<ul class="mt-4 space-y-3">
					{#each column.items as item, index (index)}
						<li class="flex items-start gap-3">
							{#if item.cost}
								<MinusIcon
									class="text-muted-foreground mt-1 size-4 shrink-0"
									aria-hidden="true"
								/>
								<span class="sr-only">{costLabel}</span>
							{:else}
								<CheckIcon
									class="text-primary mt-1 size-4 shrink-0"
									aria-hidden="true"
								/>
								<span class="sr-only">{noCostLabel}</span>
							{/if}
							<span class="text-muted-foreground text-sm">
								<RichText segments={item.segments} />
							</span>
						</li>
					{/each}
				</ul>
			</div>
		{/each}
	</div>

	<p class="border-border text-foreground mt-12 border-t pt-8 text-base font-medium">{note}</p>
</Section>
